/**
 * ClientPicker (13-24, D-13-L-7): a React Aria combobox over the client directory.
 *
 * - The business unit decides the lists (`listsForStream`): SaH and Brokered search Support at Home, NDIS searches
 *   NDIS, "Both" searches both. Active clients first; a client who is not active is listed last and marked with
 *   the list's own status value.
 * - Picking a client sets `RecipientName` (the list's name) and `RecipientClientId` (`SAH-<Id>` / `NDIS-<Id>`,
 *   D-13-24-1). Typing never picks: leaving the box without choosing an option leaves the client unpicked, and the
 *   lodge says so. Nothing is chosen for the person.
 * - "Not on the client lists" is an explicit tick: the typed name goes to `RecipientName`, the id stays blank, and
 *   the lodge Note says so (D-13-L-7).
 * - A list the person can't read (403) or that failed says so in plain words, and the other list keeps working.
 * Client names are held in memory only (`services/clientDirectory`), never in browser storage.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  Button as AriaButton,
  ComboBox,
  Input as AriaInput,
  Label,
  ListBox,
  ListBoxItem,
  Popover,
  Text,
} from 'react-aria-components';
import type { Key } from 'react-aria-components';
import { Input } from '../ui';
import { CLIENT_LISTS, inactiveNote, listsForStream } from '../../services/clientDirectory';
import type { ClientEntry, ClientSearchResult } from '../../services/clientDirectory';
import styles from './LodgeForm.module.css';

/** At most this many options are shown; the person narrows by typing. */
const SHOWN = 40;

export interface ClientPickerProps {
  /** `BusinessUnit`: decides which client lists are searched. */
  stream: string;
  search: (stream: string, query: string) => Promise<ClientSearchResult>;
  label: string;
  required: boolean;
  /** The picked client (`RecipientName` + `RecipientClientId`), if any. */
  name: string;
  clientId: string;
  unlisted: boolean;
  onPick: (entry: ClientEntry | null) => void;
  onUnlistedChange: (unlisted: boolean) => void;
  onUnlistedName: (name: string) => void;
  /** Refusal text for the name / the id, shown amber under the box. */
  message?: string;
  disabled?: boolean;
  /** Id of the focusable input (`lodge-RecipientName`), for the refusal summary's links. */
  inputId: string;
  /**
   * 13-33 (ISS-258, ISS-264): the triage mode. Only the client id is picked: no "Not on the client lists" tick, no
   * visible label (the form draws it, `label` names the box), and the search starts from `initialQuery` (the name on
   * the record). Picking never changes the record's name.
   */
  idOnly?: boolean;
  initialQuery?: string;
}

interface Loaded {
  key: string;
  result: ClientSearchResult | null;
  failed: boolean;
}

function listNames(stream: string): string {
  return listsForStream(stream)
    .map((l) => CLIENT_LISTS[l].label)
    .join(' and ');
}

export default function ClientPicker({
  stream,
  search,
  label,
  required,
  name,
  clientId,
  unlisted,
  onPick,
  onUnlistedChange,
  onUnlistedName,
  message,
  disabled,
  inputId,
  idOnly = false,
  initialQuery,
}: ClientPickerProps) {
  const [query, setQuery] = useState(idOnly ? (initialQuery ?? name) : name);
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const key = `${stream}|${query}`;

  useEffect(() => {
    if (unlisted) return;
    let cancelled = false;
    search(stream, query).then(
      (result) => {
        if (!cancelled) setLoaded({ key, result, failed: false });
      },
      () => {
        if (!cancelled) setLoaded({ key, result: null, failed: true });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [search, stream, query, key, unlisted]);

  const current = loaded?.key === key ? loaded : null;
  const matches = useMemo(() => current?.result?.matches ?? [], [current]);
  const shown = matches.slice(0, SHOWN);
  const byId = useMemo(() => new Map(matches.map((m) => [m.id, m])), [matches]);

  const messageId = message ? `${inputId}-msg` : undefined;

  function onSelectionChange(k: Key | null) {
    if (k === null) return;
    const entry = byId.get(String(k));
    if (entry) {
      onPick(entry);
      setQuery(entry.name);
    }
  }

  function onInputChange(value: string) {
    setQuery(value);
    // Typing over a picked client un-picks it; typing alone never picks one.
    if (clientId && value !== name) onPick(null);
  }

  return (
    <div className={styles.picker}>
      {!unlisted ? (
        <ComboBox
          className={styles.combo}
          inputValue={query}
          onInputChange={onInputChange}
          selectedKey={clientId || null}
          onSelectionChange={onSelectionChange}
          items={shown}
          menuTrigger="focus"
          allowsEmptyCollection
          isDisabled={disabled}
          aria-describedby={messageId}
          {...(idOnly ? { 'aria-label': label } : {})}
        >
          {!idOnly && (
            <Label className={styles.label}>
              {label}
              {required && <span className={styles.req} aria-hidden="true"> *</span>}
            </Label>
          )}
          <div className={`${styles.comboRow} ${message ? styles.inputRefused : ''}`}>
            <AriaInput id={inputId} className={styles.comboInput} placeholder={`Search the ${listNames(stream)} client list`} />
            <AriaButton className={styles.comboButton} aria-label="Show clients">
              <span aria-hidden="true">▾</span>
            </AriaButton>
          </div>
          <Text slot="description" className={styles.hint}>
            {idOnly
              ? clientId
                ? `Client id ${clientId} will be recorded. The name on the record stays as it is.`
                : 'Search the client list, then choose the client. Only their client id is recorded.'
              : clientId
                ? `Client id ${clientId} will be recorded.`
                : 'Type part of the name, then choose the client from the list.'}
          </Text>
          <Popover className={styles.comboPopover} placement="bottom start" offset={4}>
            <ListBox<ClientEntry>
              className={styles.comboList}
              renderEmptyState={() => (
                <p className={styles.comboEmpty}>
                  {current === null
                    ? 'Reading the client list…'
                    : idOnly
                      ? `No client on the ${listNames(stream)} list matches “${query}”. Check the spelling. If they aren't on a list yet, leave the id blank for now.`
                      : `No client on the ${listNames(stream)} list matches “${query}”. Check the spelling, or tick “Not on the client lists”.`}
                </p>
              )}
            >
              {(entry) => {
                const note = inactiveNote(entry);
                return (
                  <ListBoxItem id={entry.id} textValue={entry.name} className={styles.comboItem}>
                    <span className={styles.comboName}>
                      {entry.name}
                      {entry.preferredName && <span className={styles.comboKnown}> (known as {entry.preferredName})</span>}
                    </span>
                    <span className={styles.comboTags}>
                      <span className={styles.tag}>{entry.list === 'SAH' ? 'SaH' : 'NDIS'}</span>
                      {note && <span className={`${styles.tag} ${styles.tagInactive}`}>Not active · {note}</span>}
                    </span>
                  </ListBoxItem>
                );
              }}
            </ListBox>
            {matches.length > SHOWN && (
              <p className={styles.comboMore}>
                Showing {SHOWN} of {matches.length}. Keep typing to narrow it down.
              </p>
            )}
          </Popover>
        </ComboBox>
      ) : (
        <div className={styles.field}>
          <label htmlFor={inputId} className={styles.label}>
            {label}
            {required && <span className={styles.req} aria-hidden="true"> *</span>}
          </label>
          <Input
            id={inputId}
            value={name}
            disabled={disabled}
            placeholder="The client's full name"
            className={message ? styles.inputRefused : undefined}
            aria-describedby={[messageId, `${inputId}-hint`].filter(Boolean).join(' ')}
            onChange={(e) => onUnlistedName(e.target.value)}
          />
          <p id={`${inputId}-hint`} className={styles.hint}>
            The client id stays blank. Whoever triages it adds the id once the client is on a list.
          </p>
        </div>
      )}

      {message && (
        <p id={`${inputId}-msg`} className={styles.refusal}>
          {message}
        </p>
      )}

      {!unlisted &&
        current?.result?.unavailable.map((u) => (
          <p key={u.list} className={styles.notice} role="status">
            {u.reason === 'forbidden'
              ? idOnly
                ? `The ${CLIENT_LISTS[u.list].label} client list isn't available to you, so its clients can't be picked here.`
                : `The ${CLIENT_LISTS[u.list].label} client list isn't available to you. Tick “Not on the client lists” and type the name.`
              : idOnly
                ? `Couldn't read the ${CLIENT_LISTS[u.list].label} client list just now. Try again later.`
                : `Couldn't read the ${CLIENT_LISTS[u.list].label} client list just now. Try again, or tick “Not on the client lists”.`}
          </p>
        ))}
      {!unlisted && current?.failed && (
        <p className={styles.notice} role="status">
          {idOnly ? "Couldn't read the client lists just now. Try again later." : 'Couldn\'t read the client lists just now. Try again, or tick “Not on the client lists”.'}
        </p>
      )}

      {!idOnly && (
      <label className={styles.tick}>
        <input
          type="checkbox"
          checked={unlisted}
          disabled={disabled}
          onChange={(e) => {
            setQuery('');
            onUnlistedChange(e.target.checked);
          }}
        />
        <span>Not on the client lists</span>
      </label>
      )}
    </div>
  );
}
